import { Component, Inject, Prop } from "vue-property-decorator";
import { AbsencesPlanningMensuelDto } from "../../../../dto/GenApi.iNotPaie.Dto/AbsencesPlanningMensuelDto";
import { ActionAbsencesEnum } from "../../../../dto/GenApi.iNotPaie.Dto/ActionAbsencesEnum";
import { getAbsenceActionsState } from "../../../../Helper/absenceActionsHelper";
import {
    cleanPopperElm,
    cleanTooltipPopper, continuesLeft as continuesLeftHelper,
    continuesRight as continuesRightHelper, convertAbsenceToSidePanelHistorique,
    executeChangementEtatAbsence, getAbsenceStyle,
    getBarPositionPercentages,
    getBorderRadiusClass,
    getCancelAbsenceModalOptions,
    getCancelCancellationModalOptions,
    getDayIndexInCalendar,
    getDisplayAbsence, getDureeAbsence,
    getLibelleAbsence, getStateInfo,
    getTypeIcon,
    isValidatedPlanning
} from "../../../../Helper/absencePlanningHelper";
import { CalendarDay, IAbsenceStyle } from "../../../../shared/interfaces/calendar.interface";
import { ISidePanelHistorique } from "../../../../shared/interfaces/sidePanelHistorique.interface";
import { NotificationServiceInstance } from "../../../../shared/services/notification.service";
import { EventBus } from "../../../Controls/EventBus";
import EventConstants from "../../../Controls/EventConstants";
import { VueBase } from "../../../Controls/VueBase";

/**
 * Composant d'une barre d'absence dans le calendrier équipe (une ligne par salarié, une barre par absence sur la plage du mois).
 * Gère la position en % (left/width), les demi-journées, les bords tronqués, le mode anonymisé et le menu.
 */
@Component({
    components: {
        SidePanelHistorique: require("../../sidePanelHistorique/sidePanelHistorique.vue.html")
    }
})
export default class AbsencePlanningEquipe extends VueBase {
    @Prop({ required: true }) public absence!: AbsencesPlanningMensuelDto;
    /** Tous les jours du mois affichés (grille horizontale) : utilisé pour calculer left/width en %. */
    @Prop({ required: true }) public calendarDays!: CalendarDay[];
    @Prop({ required: true }) public isEditable!: boolean;

    @Inject("modifyAbsence") public readonly modifyAbsence!: (id: number) => void;

    public drawerAbsence: ISidePanelHistorique | null = null;
    public drawerIsVisible = false;
    public menuPopoverVisible = false;

    /** Absence sans droit de détail (état masqué) : affichage "Accès limité". */
    public get isAnonymized(): boolean {
        return this.absence.IsAnonyme && !this.isEditable;
    }

    /** Libellé affiché, libellé pour le tooltip et couleur (un seul appel au helper). */
    public get display(): { libelle: string; libelleTooltip: string; color: string } {
        return getDisplayAbsence(this.absence.LibelleTypeAbsence, this.absence.Color, this.isAnonymized);
    }

    /** L'absence débute avant le premier jour du calendrier affiché (barre tronquée à gauche). */
    private continuesLeft(): boolean {
        const totalDaysInCalendar = this.calendarDays.length;

        return totalDaysInCalendar > 0 && continuesLeftHelper(this.absence.DateDebut, this.calendarDays[0].date);
    }

    /** L'absence se termine après le dernier jour du calendrier affiché (barre tronquée à droite). */
    private continuesRight(): boolean {
        const totalDaysInCalendar = this.calendarDays.length;

        return totalDaysInCalendar > 0 && continuesRightHelper(this.absence.DateFin, this.calendarDays[totalDaysInCalendar - 1].date);
    }

    /** Index (0-based) du premier jour de l'absence dans la liste calendarDays. 0 si déborde à gauche. */
    private startDayIndex(): number {
        return this.continuesLeft() ? 0 : getDayIndexInCalendar(this.absence.DateDebut, this.calendarDays, 0);
    }

    /** Index (0-based) du dernier jour de l'absence dans la liste calendarDays. Dernier index si déborde à droite. */
    private endDayIndex(): number {
        const lastIndex = this.calendarDays.length - 1;

        return this.continuesRight() ? lastIndex : getDayIndexInCalendar(this.absence.DateFin, this.calendarDays, lastIndex);
    }

    /** True si l'absence commence en demi-journée (après-midi) sur le premier jour affiché. */
    private halfDayStart(): boolean {
        return !this.continuesLeft() && this.absence.DebutMatinAprem === "A";
    }

    /** True si l'absence se termine en demi-journée (matin) sur le dernier jour affiché. */
    private halfDayEnd(): boolean {
        return !this.continuesRight() && this.absence.FinMatinAprem === "M";
    }

    /**
     * Style inline pour la barre d'absence : couleurs (fond, texte, bordure) + position (left %, width %).
     * Utilisé par le template sur l'élément de la barre. La position place la barre sur la grille du mois.
     */
    public get barStyle(): Record<string, string> {
        const totalDaysInCalendar = this.calendarDays.length;
        let barLeftPercent = "0%";
        let barWidthPercent = "100%";

        // Si le mois a des jours, on calcule la position et la largeur de la barre
        if (totalDaysInCalendar > 0) {
            const positionInGrid = getBarPositionPercentages(
                totalDaysInCalendar,
                this.continuesLeft(),
                this.continuesRight(),
                this.startDayIndex(),
                this.endDayIndex(),
                this.halfDayStart(),
                this.halfDayEnd()
            );

            barLeftPercent = `${positionInGrid.left}%`;
            barWidthPercent = `${positionInGrid.width}%`;
        }

        // On récupère le style de la barre
        const absenceVisualStyle = getAbsenceStyle(this.absence.Color as string, isValidatedPlanning(this.absence.Etat), this.isAnonymized);

        return {
            backgroundColor: absenceVisualStyle.backgroundColor,
            color: absenceVisualStyle.color,
            borderColor: absenceVisualStyle.borderColor,
            borderStyle: absenceVisualStyle.borderStyle,
            left: barLeftPercent,
            width: barWidthPercent
        };
    }

    /** En mode équipe une seule barre par absence : on affiche toujours le détail. */
    public get showText(): boolean {
        return true;
    }

    /** Classe CSS pour les arrondis de la barre : single | start-only | end-only | middle. */
    private borderRadiusClass(): string {
        return getBorderRadiusClass(this.continuesLeft(), this.continuesRight());
    }

    /** Données d'affichage pour le template : style, icône du type, libellé/icône d'état. */
    public get displayInfo(): { style: IAbsenceStyle; typeIcon: string; stateInfo: { title: string; icon: string } } {
        return {
            style: getAbsenceStyle(this.absence.Color as string, isValidatedPlanning(this.absence.Etat), this.isAnonymized),
            typeIcon: getTypeIcon(this.absence.CodeTypeAbsence, this.isAnonymized),
            stateInfo: getStateInfo(this.absence.Etat, this.isAnonymized)
        };
    }

    public getDuration(): string {
        return getDureeAbsence(this.absence.NombreJours, this.absence.NombreHeures);
    }

    public onTooltipHide(): void {
        cleanTooltipPopper(this.$refs.tooltip as any);
    }

    public onPopOverHide(): void {
        cleanPopperElm(this.$refs.menuPopover as any);
    }

    public onAbsenceMouseEnter(): void {
        (this.$el as HTMLElement)?.querySelector(".absence-line")?.classList.add("hover-group-absence");
    }

    public onAbsenceMouseLeave(): void {
        this.onTooltipHide();
        (this.$el as HTMLElement)?.querySelector(".absence-line")?.classList.remove("hover-group-absence");
    }

    public onAbsenceClicked(event?: MouseEvent): void {
        if (!this.isEditable) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            return;
        }

        this.menuPopoverVisible = !this.menuPopoverVisible;

        if (this.menuPopoverVisible) {
            this.onTooltipHide();
        } else {
            this.onPopOverHide();
        }
    }

    public onAbsenceClickedOutside(): void {
        if (this.menuPopoverVisible) {
            this.menuPopoverVisible = false;
            this.onPopOverHide();
        }
    }

    public openDrawerHistorique(absence: AbsencesPlanningMensuelDto): void {
        this.menuPopoverVisible = false;
        this.$nextTick(() => {
            this.drawerAbsence = this.convertAbsenceToDisplay(absence);
            this.drawerIsVisible = true;
        });
    }

    public get actionsState() {
        return getAbsenceActionsState({
            etat: this.absence.Etat!,
            poseeParGestionnaire: this.absence.PoseeParGestionnaire,
            isAnonymized: this.isAnonymized,
        });
    }

    public modify(): void {
        this.menuPopoverVisible = false;
        this.modifyAbsence(this.absence.Id);
    }

    public cancel(): void {
        this.menuPopoverVisible = false;
        EventBus.$emit(
            EventConstants.MODAL_ALERT,
            getCancelAbsenceModalOptions(() =>
                this.changeEtatAbsence(
                    ActionAbsencesEnum.DemanderAnnulerAbsenceParSalarie,
                    "L'annulation de l'absence n'a pas pu être effectuée."
                )
            )
        );
    }

    public cancelCancellation(): void {
        this.menuPopoverVisible = false;
        EventBus.$emit(
            EventConstants.MODAL_ALERT,
            getCancelCancellationModalOptions(() =>
                this.changeEtatAbsence(
                    ActionAbsencesEnum.AnnulerDemandeAnnulationAbsenceParSalarie,
                    "La suppression de la demande d'annulation de l'absence n'a pas pu être effectuée."
                )
            )
        );
    }

    private changeEtatAbsence(action: ActionAbsencesEnum, messageErreur: string): void {
        executeChangementEtatAbsence(
            { IdAbsence: this.absence.Id, Action: action, Etat: this.absence.Etat! },
            () => EventBus.$emit(EventConstants.UPDATE_CONGES),
            () => NotificationServiceInstance.SendFailed(messageErreur)
        );
    }

    private convertAbsenceToDisplay(absence: AbsencesPlanningMensuelDto): ISidePanelHistorique {
        const typeLabel = this.isAnonymized ? "Absence - Accès limité" : getLibelleAbsence(absence.LibelleTypeAbsence, absence.PoseeParGestionnaire);

        return convertAbsenceToSidePanelHistorique(absence, typeLabel);
    }
}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/calendrierEquipe/absencePlanningEquipe/absencePlanningEquipe.ts